'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus, X, Loader2, ClipboardList, DollarSign } from 'lucide-react';
import {
  householdsAPI,
  HouseholdMember,
  HouseholdService,
  HouseholdServiceCreate,
  ServiceStatus,
} from '@/services/api/households';

const SERVICE_STATUS_COLORS: Record<ServiceStatus, string> = {
  IDENTIFIED: 'bg-yellow-100 text-yellow-800',
  IN_PROGRESS: 'bg-blue-100 text-blue-800',
  COMPLETED: 'bg-green-100 text-green-800',
  DENIED: 'bg-red-100 text-red-800',
  CANCELLED: 'bg-gray-100 text-gray-800',
};

const SERVICE_STATUS_LABELS: Record<ServiceStatus, string> = {
  IDENTIFIED: 'Identified',
  IN_PROGRESS: 'In Progress',
  COMPLETED: 'Completed',
  DENIED: 'Denied',
  CANCELLED: 'Cancelled',
};

interface Props {
  householdId: number;
  members: HouseholdMember[];
}

export default function HouseholdServicesTab({ householdId, members }: Props) {
  const [services, setServices] = useState<HouseholdService[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<HouseholdServiceCreate>({
    service_type: '',
  });

  const loadServices = useCallback(async () => {
    setLoading(true);
    try {
      const data = await householdsAPI.listServices(householdId);
      setServices(data);
    } catch (err) {
      console.error('Failed to load services:', err);
    } finally {
      setLoading(false);
    }
  }, [householdId]);

  useEffect(() => {
    loadServices();
  }, [loadServices]);

  const handleCreate = async () => {
    if (!form.service_type) return;
    setSaving(true);
    try {
      await householdsAPI.addService(householdId, form);
      setShowAdd(false);
      setForm({ service_type: '' });
      loadServices();
    } catch (err) {
      console.error('Failed to create service:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (serviceId: number, newStatus: ServiceStatus) => {
    try {
      await householdsAPI.updateService(householdId, serviceId, { service_status: newStatus });
      loadServices();
    } catch (err) {
      console.error('Failed to update service:', err);
    }
  };

  const activeMembers = members.filter((m) => m.status === 'ACTIVE');
  const fmt = (n?: number) => (n != null ? `$${n.toLocaleString('en-US', { minimumFractionDigits: 2 })}` : '-');

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Services & Interventions</h3>
        <Button size="sm" onClick={() => setShowAdd(!showAdd)} className="gap-1.5">
          <Plus className="w-4 h-4" />
          Add Service
        </Button>
      </div>

      {/* Add Service Form */}
      {showAdd && (
        <Card className="border-indigo-200 bg-indigo-50/30">
          <CardContent className="p-4 space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium">Add Service / Intervention</h4>
              <button onClick={() => setShowAdd(false)}>
                <X className="w-4 h-4 text-gray-400" />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Service For</Label>
                <select
                  value={form.household_member_id || ''}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      household_member_id: e.target.value ? Number(e.target.value) : undefined,
                    }))
                  }
                  className="w-full border rounded-md px-3 py-2 text-sm"
                >
                  <option value="">Entire Household</option>
                  {activeMembers.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.first_name} {m.last_name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Service Type</Label>
                <Input
                  value={form.service_type}
                  onChange={(e) => setForm((prev) => ({ ...prev, service_type: e.target.value }))}
                  placeholder="e.g. Housing Assistance, Transportation"
                />
              </div>
            </div>

            <div>
              <Label>Description</Label>
              <textarea
                value={form.service_description || ''}
                onChange={(e) => setForm((prev) => ({ ...prev, service_description: e.target.value }))}
                className="w-full border rounded-md px-3 py-2 text-sm min-h-[60px]"
                placeholder="Describe the service or intervention..."
              />
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Service Provider</Label>
                <Input
                  value={form.service_provider || ''}
                  onChange={(e) => setForm((prev) => ({ ...prev, service_provider: e.target.value }))}
                  placeholder="Provider name"
                />
              </div>
              <div>
                <Label>Funding Source</Label>
                <Input
                  value={form.funding_source || ''}
                  onChange={(e) => setForm((prev) => ({ ...prev, funding_source: e.target.value }))}
                  placeholder="e.g. FEMA, NGO"
                />
              </div>
              <div>
                <Label>Amount Requested</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={form.amount_requested || ''}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, amount_requested: Number(e.target.value) || undefined }))
                  }
                  placeholder="0.00"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setShowAdd(false)}>
                Cancel
              </Button>
              <Button size="sm" disabled={!form.service_type || saving} onClick={handleCreate}>
                {saving ? 'Creating...' : 'Add Service'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Services List */}
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-5 h-5 animate-spin text-indigo-600" />
        </div>
      ) : services.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center">
            <ClipboardList className="w-10 h-10 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-500">No services or interventions recorded</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {services.map((svc) => (
            <Card key={svc.id}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-sm">{svc.service_type}</span>
                      <Badge
                        className={SERVICE_STATUS_COLORS[svc.service_status] || 'bg-gray-100 text-gray-800'}
                        variant="secondary"
                      >
                        {SERVICE_STATUS_LABELS[svc.service_status] || svc.service_status}
                      </Badge>
                      <Badge variant="outline" className="text-xs">
                        {svc.service_for || 'Household'}
                      </Badge>
                    </div>
                    {svc.service_description && (
                      <p className="text-sm text-gray-600">{svc.service_description}</p>
                    )}
                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                      {svc.service_provider && <span>Provider: {svc.service_provider}</span>}
                      {svc.funding_source && <span>Funding: {svc.funding_source}</span>}
                      {(svc.amount_requested || svc.amount_approved || svc.amount_disbursed) && (
                        <span className="flex items-center gap-1">
                          <DollarSign className="w-3 h-3" />
                          Req: {fmt(svc.amount_requested)} | Appr: {fmt(svc.amount_approved)} | Disb:{' '}
                          {fmt(svc.amount_disbursed)}
                        </span>
                      )}
                    </div>
                    {svc.notes && <p className="text-xs text-gray-400 mt-1">{svc.notes}</p>}
                  </div>

                  <select
                    value={svc.service_status}
                    onChange={(e) => handleStatusChange(svc.id, e.target.value as ServiceStatus)}
                    className="border rounded px-2 py-1 text-xs"
                  >
                    {Object.entries(SERVICE_STATUS_LABELS).map(([val, label]) => (
                      <option key={val} value={val}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
